'use client';

import { cn } from '@sharpr/ui';
import { CloudRain, Wind, Snowflake, Sun, Droplets, Home, CloudSun, Cloud, TriangleAlert } from 'lucide-react';
import type { GameWeather } from '@/services/weather/types';

interface WeatherDetailCardProps {
  weather: GameWeather;
  className?: string;
}

// ─── Severity ──────────────────────────────────────────────────────

type Severity = {
  label: string;
  tone: 'red' | 'amber' | 'blue' | 'cyan' | 'orange' | 'teal' | 'purple' | 'slate';
  headline: string | null;
  Icon: React.ComponentType<{ className?: string }>;
};

function severity(w: GameWeather): Severity {
  const f = w.flags;
  const pct = w.precip.probabilityPct;
  if (f.rainoutRisk === 'high')
    return { label: `${pct}% RAIN`, tone: 'red', headline: `Rainout risk — ${pct}% rain at first pitch`, Icon: CloudRain };
  if (f.rainoutRisk === 'medium')
    return { label: `${pct}% RAIN`, tone: 'amber', headline: `${pct}% chance of rain — possible delay`, Icon: CloudRain };
  if (f.windProfile === 'out')
    return { label: `WIND OUT`, tone: 'amber', headline: `${Math.round(w.wind.speedMph)} mph blowing out — HR boost`, Icon: Wind };
  if (f.windProfile === 'in')
    return { label: `WIND IN`, tone: 'blue', headline: `${Math.round(w.wind.speedMph)} mph blowing in — HR suppressed`, Icon: Wind };
  if (f.windProfile === 'crosswind')
    return { label: `CROSS`, tone: 'purple', headline: `${Math.round(w.wind.speedMph)} mph crosswind`, Icon: Wind };
  if (f.rainoutRisk === 'low')
    return { label: `${pct}% RAIN`, tone: 'blue', headline: `${pct}% rain — low risk`, Icon: CloudRain };
  if (f.extremeCold)
    return { label: `COLD`, tone: 'cyan', headline: `Cold (${Math.round(w.feelsLikeF)}°) — carry suppressed`, Icon: Snowflake };
  if (f.extremeHeat)
    return { label: `HOT`, tone: 'orange', headline: `Hot (${Math.round(w.feelsLikeF)}°)`, Icon: Sun };
  return { label: `CLEAR`, tone: 'slate', headline: null, Icon: CloudSun };
}

const TONE_CLASSES: Record<Severity['tone'], { badge: string; text: string; bar: string }> = {
  red:    { badge: 'bg-red-500/15 text-red-600 dark:text-red-400',       text: 'text-red-600 dark:text-red-400',       bar: 'bg-red-500/70' },
  amber:  { badge: 'bg-amber-500/15 text-amber-600 dark:text-amber-400', text: 'text-amber-600 dark:text-amber-400',   bar: 'bg-amber-500/70' },
  blue:   { badge: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',    text: 'text-blue-600 dark:text-blue-400',     bar: 'bg-blue-500/70' },
  cyan:   { badge: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400',    text: 'text-cyan-600 dark:text-cyan-400',     bar: 'bg-cyan-500/70' },
  orange: { badge: 'bg-orange-500/15 text-orange-600 dark:text-orange-400', text: 'text-orange-600 dark:text-orange-400', bar: 'bg-orange-500/70' },
  teal:   { badge: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',    text: 'text-teal-600 dark:text-teal-400',     bar: 'bg-teal-500/70' },
  purple: { badge: 'bg-purple-500/15 text-purple-600 dark:text-purple-400', text: 'text-purple-600 dark:text-purple-400', bar: 'bg-purple-500/70' },
  slate:  { badge: 'bg-slate-500/10 text-slate-600 dark:text-slate-300', text: 'text-slate-600 dark:text-slate-300',   bar: 'bg-slate-500/40' },
};

// ─── Compass ──────────────────────────────────────────────────────

function WindCompass({ windFromDeg, cfBearingDeg }: { windFromDeg: number; cfBearingDeg: number }) {
  const windToDeg = (windFromDeg + 180) % 360;
  const relToDiamond = ((windToDeg - cfBearingDeg) + 360) % 360; // 0=out to CF
  const size = 80;
  const cx = size / 2;
  const cy = size / 2;
  const r = 34;
  const arrowLen = r * 0.75;
  const rad = (relToDiamond - 90) * (Math.PI / 180);
  const ax = cx + Math.cos(rad) * arrowLen;
  const ay = cy + Math.sin(rad) * arrowLen;
  const tailX = cx - Math.cos(rad) * arrowLen * 0.25;
  const tailY = cy - Math.sin(rad) * arrowLen * 0.25;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0 text-foreground dark:text-text-primary">
      <defs>
        <marker id="wcd-arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
        </marker>
      </defs>
      {/* Outer ring */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="1" />
      {/* Diamond (home at bottom, CF at top) */}
      <polygon
        points={`${cx},${cy - r * 0.6} ${cx + r * 0.5},${cy} ${cx},${cy + r * 0.5} ${cx - r * 0.5},${cy}`}
        fill="currentColor"
        fillOpacity="0.06"
        stroke="currentColor"
        strokeOpacity="0.35"
        strokeWidth="1"
      />
      {/* CF marker (above ring) */}
      <text x={cx} y={cy - r + 2} textAnchor="middle" fontSize="8" fontWeight="600" fill="currentColor" fillOpacity="0.55">CF</text>
      {/* Home plate */}
      <path
        d={`M ${cx - 3} ${cy + r * 0.5 + 2} L ${cx + 3} ${cy + r * 0.5 + 2} L ${cx + 3} ${cy + r * 0.5 + 5} L ${cx} ${cy + r * 0.5 + 8} L ${cx - 3} ${cy + r * 0.5 + 5} Z`}
        fill="currentColor"
        fillOpacity="0.55"
      />
      {/* Wind arrow — thicker with tail and arrowhead */}
      <line
        x1={tailX}
        y1={tailY}
        x2={ax}
        y2={ay}
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        markerEnd="url(#wcd-arrow)"
      />
    </svg>
  );
}

// ─── Precipitation timeline ──────────────────────────────────────

function PrecipTimeline({ hourly, venueTimezone }: { hourly: GameWeather['hourly']; venueTimezone: string }) {
  if (!hourly.length) return null;
  const maxHeight = 36;
  const slots = hourly.slice(0, 4);

  return (
    <div className="flex items-end justify-between gap-1.5" style={{ height: maxHeight + 24 }}>
      {slots.map((h, i) => {
        const pct = Math.max(0, Math.min(100, h.precipPct));
        const barH = Math.max(3, (pct / 100) * maxHeight);
        const color =
          pct >= 75 ? 'bg-red-500/80' :
          pct >= 50 ? 'bg-amber-500/80' :
          pct >= 20 ? 'bg-blue-500/70' :
                      'bg-slate-400/30 dark:bg-slate-500/25';
        const label = new Date(h.iso)
          .toLocaleTimeString('en-US', { hour: 'numeric', timeZone: venueTimezone })
          .replace(' ', '')
          .toLowerCase();
        const pctLabelTone =
          pct >= 75 ? 'text-red-600 dark:text-red-400 font-semibold' :
          pct >= 50 ? 'text-amber-600 dark:text-amber-400 font-semibold' :
          pct >= 20 ? 'text-blue-600 dark:text-blue-400' :
                      'text-muted-foreground dark:text-text-tertiary';
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
            <div className={cn('text-[10px] leading-none tabular-nums', pctLabelTone)}>
              {pct}%
            </div>
            <div className={cn('w-full rounded-sm', color)} style={{ height: barH }} />
            <div className="text-[9px] leading-none text-muted-foreground/70 dark:text-text-tertiary">
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Direction helpers ───────────────────────────────────────────

function dirToCardinal(deg: number): string {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(((deg % 360) / 22.5)) % 16];
}

function ymdInZone(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function formatFirstPitch(iso: string, timeZone: string): string {
  const date = new Date(iso);
  try {
    const time = date
      .toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
        timeZoneName: 'short',
      })
      .replace(/\s+/g, ' ')
      .trim();
    const isToday = ymdInZone(date, timeZone) === ymdInZone(new Date(), timeZone);
    if (isToday) return time;
    const day = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(date);
    return `${day} · ${time}`;
  } catch {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
}

function hourLabel(d: Date, timeZone: string): { hour: string; suffix: 'am' | 'pm' } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    hour12: true,
  }).formatToParts(d);
  const hour = parts.find(p => p.type === 'hour')?.value ?? '';
  const suffix = (parts.find(p => p.type === 'dayPeriod')?.value ?? 'am').toLowerCase() as 'am' | 'pm';
  return { hour, suffix };
}

function formatHourRange(hourly: GameWeather['hourly'], timeZone: string): string {
  if (!hourly.length) return 'first pitch +4h';
  const start = new Date(hourly[0].iso);
  const last = hourly[hourly.length - 1];
  const end = new Date(new Date(last.iso).getTime() + 60 * 60 * 1000);
  const a = hourLabel(start, timeZone);
  const b = hourLabel(end, timeZone);
  return a.suffix === b.suffix ? `${a.hour}–${b.hour}${b.suffix}` : `${a.hour}${a.suffix}–${b.hour}${b.suffix}`;
}

function relativeName(rel: GameWeather['wind']['relativeToCf']): string {
  switch (rel) {
    case 'out':  return 'Out to CF';
    case 'in':   return 'In from CF';
    case 'cross-lr': return 'Cross L→R';
    case 'cross-rl': return 'Cross R→L';
    case 'calm': return 'Calm';
  }
}

// ─── Main ────────────────────────────────────────────────────────

export function WeatherDetailCard({ weather, className }: WeatherDetailCardProps) {
  const w = weather;

  const firstPitchLabel = formatFirstPitch(w.firstPitchIso, w.venueTimezone);

  if (w.indoorVenue) {
    return (
      <div className={cn(
        'pointer-events-auto rounded-lg shadow-xl bg-white dark:bg-bg-secondary border border-gray-200 dark:border-border-primary w-64 text-fg dark:text-text-primary overflow-hidden',
        className,
      )}>
        <div className="px-3 py-2.5 flex items-center gap-2 bg-slate-500/10 border-b border-gray-200 dark:border-border-primary">
          <Home className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-xs font-semibold leading-tight">Indoor · controlled</div>
            <div className="text-[11px] text-muted-foreground dark:text-text-tertiary leading-tight">
              {w.roof === 'Retractable' ? 'Retractable roof' : 'Fixed dome'} — weather muted
            </div>
          </div>
        </div>
        <div className="px-3 py-1.5 text-[10px] text-muted-foreground dark:text-text-tertiary border-b border-gray-200 dark:border-border-primary">
          First pitch · <span className="font-semibold text-foreground dark:text-text-primary tabular-nums">{firstPitchLabel}</span>
        </div>
        <div className="px-3 py-2 text-[10px] text-muted-foreground/60 dark:text-text-tertiary/60">
          Data: Open-Meteo.com
        </div>
      </div>
    );
  }

  const sev = severity(w);
  const tone = TONE_CLASSES[sev.tone];
  const windDir = dirToCardinal(w.wind.directionDeg);
  const rel = relativeName(w.wind.relativeToCf);
  const isAlertTone = sev.tone === 'red' || sev.tone === 'amber';

  return (
    <div className={cn(
      'pointer-events-auto rounded-lg shadow-xl bg-white dark:bg-bg-secondary border border-gray-200 dark:border-border-primary w-[300px] text-fg dark:text-text-primary overflow-hidden',
      className,
    )}>
      {/* Header: temp + severity badge */}
      <div className="px-3 pt-2.5 pb-2 flex items-start justify-between gap-2">
        <div>
          <div className="flex items-baseline gap-1.5 leading-none">
            <span className="text-2xl font-bold tabular-nums">{Math.round(w.tempF)}°</span>
            <span className="text-[11px] text-muted-foreground dark:text-text-tertiary">
              feels {Math.round(w.feelsLikeF)}°
            </span>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground dark:text-text-tertiary">
            First pitch · <span className="font-semibold text-foreground dark:text-text-primary tabular-nums">{firstPitchLabel}</span>
          </div>
        </div>
        <div className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide',
          tone.badge,
        )}>
          <sev.Icon className="w-3 h-3" />
          {sev.label}
        </div>
      </div>

      {/* Verdict band (only for actionable conditions) */}
      {sev.headline && (
        <div className={cn(
          'px-3 py-1.5 flex items-center gap-1.5 text-[11px] font-medium border-y',
          isAlertTone ? 'bg-red-500/5 border-red-500/20 dark:border-red-500/20' :
          'bg-muted/30 dark:bg-white/5 border-gray-200 dark:border-border-primary',
          tone.text,
        )}>
          {isAlertTone && <TriangleAlert className="w-3.5 h-3.5 flex-shrink-0" />}
          <span>{sev.headline}</span>
        </div>
      )}

      {/* Compass + wind data */}
      <div className="px-3 py-2.5 flex items-center gap-3">
        <WindCompass
          windFromDeg={w.wind.directionDeg}
          cfBearingDeg={w.cfBearingDeg}
        />
        <div className="flex-1 min-w-0 space-y-1">
          <div>
            <div className="text-[10px] text-muted-foreground dark:text-text-tertiary uppercase tracking-wide">Wind</div>
            <div className="flex items-baseline gap-1 leading-tight">
              <span className="text-base font-semibold tabular-nums">{Math.round(w.wind.speedMph)}</span>
              <span className="text-[10px] text-muted-foreground dark:text-text-tertiary">mph {windDir}</span>
            </div>
            <div className={cn('text-[10px] font-medium', tone.text)}>{rel}</div>
          </div>
          {w.wind.gustsMph > w.wind.speedMph + 2 && (
            <div className="text-[10px] text-muted-foreground dark:text-text-tertiary">
              Gusts to <span className="font-semibold text-foreground dark:text-text-primary tabular-nums">{Math.round(w.wind.gustsMph)}</span> mph
            </div>
          )}
        </div>
      </div>

      {/* Precip timeline */}
      <div className="px-3 pb-2">
        <div className="flex items-center justify-between text-[10px] text-muted-foreground dark:text-text-tertiary uppercase tracking-wide mb-1.5">
          <span>Rain · {formatHourRange(w.hourly, w.venueTimezone)}</span>
          {w.precip.amountIn > 0.01 && (
            <span className="font-mono normal-case tracking-normal">
              {w.precip.amountIn.toFixed(2)}″
            </span>
          )}
        </div>
        <PrecipTimeline hourly={w.hourly} venueTimezone={w.venueTimezone} />
      </div>

      {/* Secondary stats + attribution */}
      <div className="px-3 pt-1.5 pb-2 border-t border-gray-200 dark:border-border-primary flex items-center justify-between text-[10px]">
        <div className="flex items-center gap-2.5 text-muted-foreground dark:text-text-tertiary">
          <span className="inline-flex items-center gap-1">
            <Droplets className="w-3 h-3" />
            <span className="tabular-nums">{Math.round(w.humidity)}%</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Cloud className="w-3 h-3" />
            <span className="tabular-nums">{Math.round(w.cloudCover)}%</span>
          </span>
        </div>
        <span className="text-muted-foreground/50 dark:text-text-tertiary/50">Open-Meteo</span>
      </div>
    </div>
  );
}
